import { useSyncExternalStore } from 'react'

/**
 * Bridge store between the Workbench chart and the chat, which live in separate
 * subtrees under BotDetail — the chartPush pattern (module state + listeners +
 * per-bid `ensure`). Needed because the Workbench, and with it the chart engine,
 * unmounts on the Backtest and Optimise tabs while the chat column stays mounted.
 *
 * It carries two answers with DELIBERATELY OPPOSITE semantics — see each field.
 *
 * `hasIdea`: has the user saved a trade idea on this strategy? The first one opens
 * the chat on a phone (where it is a closed pill), onto the composer's "Send chart
 * to AI" tab — teaching someone who has not talked to the AI yet that marking up
 * the chart is only half the job.
 * A one-way LATCH, not a live mirror: it is what makes the nudge a once-ever
 * event, and it must not come back when the user deletes the idea or switches to
 * a market that has none (chart state is per symbol).
 *
 * `drawCount`: how many drawings are on the canvas RIGHT NOW. A live mirror, the
 * exact opposite of the latch above — it has to fall back to 0 when the user
 * deletes their drawings or switches to an unmarked market, because it decides
 * whether the chat may offer a walkthrough (which replays the period drawn on).
 */

/**
 * Mirrored to localStorage for one reason: the truth lives in the workbench blob,
 * but that is a 1500ms debounced autosave away — a save-then-instant-reload would
 * otherwise replay the nudge on idea #2. A pure cache of server truth, and
 * self-healing: the latch is one-way, so the blob never contradicts it.
 */
const IDEA_PREFIX = 'algobuilder.ideaSaved.'

/**
 * The live drawing count needs its own mirror for a different reason: the chat can
 * ask the question on a tab where the Workbench never mounted at all. A reload
 * straight onto ?tab=backtest leaves nothing to publish the count, and reading 0
 * there would offer a backtest to someone who has drawn their whole setup. A pure
 * cache of the last count this browser saw, overwritten the moment the Workbench
 * mounts — so a stale value can only survive while nothing can contradict it.
 */
const DRAW_PREFIX = 'algobuilder.drawCount.'

function loadFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function saveFlag(key: string) {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // storage unavailable — the hint just re-derives from the workbench blob
  }
}

function loadCount(key: string): number {
  try {
    const n = Number(localStorage.getItem(key))
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
  } catch {
    return 0
  }
}

function saveCount(key: string, n: number) {
  try {
    localStorage.setItem(key, String(n))
  } catch {
    // storage unavailable — the count just re-derives when the Workbench mounts
  }
}

export interface ChartActivityState {
  bid: string | null
  /** Sticky per strategy: it has had a trade idea on it — placed now, or already
   *  there when it loaded. THE once-ever gate for the composer nudge. */
  hasIdea: boolean
  /** One-shot, deliberately NOT persisted: "open the chat onto it NOW". A latch
   *  alone cannot drive that — the consumer needs an EDGE. Reading `hasIdea` at
   *  mount instead would replay it on every remount. */
  composerNudge: boolean
  /** LIVE, for the active symbol: drawings currently on the canvas. Falls back to
   *  0 on delete and on a switch to an unmarked market — unlike `hasIdea`. */
  drawCount: number
}

const fresh = (bid: string | null): ChartActivityState => ({
  bid,
  hasIdea: bid ? loadFlag(IDEA_PREFIX + bid) : false,
  composerNudge: false,
  drawCount: bid ? loadCount(DRAW_PREFIX + bid) : 0,
})

let state: ChartActivityState = fresh(null)
const listeners = new Set<() => void>()
// Stable per-bid fallback so getSnapshot stays pure — and does not hit
// localStorage on every render — while the store still holds another bot's state.
let freshSnap: ChartActivityState = fresh(null)

function emit() {
  listeners.forEach((l) => l())
}

/** Reset to a clean slate when a different bot's workbench/chat touches the store. */
function ensure(bid: string) {
  if (state.bid !== bid) {
    state = fresh(bid)
    emit()
  }
}

/** Pure read for render paths: never mutates, never emits. */
function snapshotFor(bid: string): ChartActivityState {
  if (state.bid === bid) return state
  if (freshSnap.bid !== bid) freshSnap = fresh(bid)
  return freshSnap
}

/** One writer for the idea latch; `nudge` says whether it also raises the one-shot. */
function latchIdea(bid: string, nudge: boolean) {
  ensure(bid)
  if (state.hasIdea) return // the once-ever, and what makes idea #2 silent
  state = { ...state, hasIdea: true, composerNudge: nudge }
  saveFlag(IDEA_PREFIX + bid)
  emit()
}

export const chartActivity = {
  /** Read for event handlers — adopts the bid (resets if it changed). */
  get(bid: string): ChartActivityState {
    ensure(bid)
    return state
  },

  ensure,

  subscribe(l: () => void): () => void {
    listeners.add(l)
    return () => listeners.delete(l)
  },

  /** The user just left their first trade idea: latch it AND raise the nudge. */
  markIdeaSaved(bid: string) {
    latchIdea(bid, true)
  },

  /** Load-time seed from the persisted chart: this strategy already had ideas, so
   *  the nudge was spent in some earlier session. Latch, stay quiet. */
  seedIdeaSaved(bid: string) {
    latchIdea(bid, false)
  },

  /** The Workbench republishing its live drawing count. Written from the rendered
   *  `drawCount` state rather than the engine, so the chat's walkthrough offer and
   *  the toolbar's own gate can never read different numbers. */
  setDrawCount(bid: string, n: number) {
    ensure(bid)
    const next = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
    if (state.drawCount === next) return
    state = { ...state, drawCount: next }
    saveCount(DRAW_PREFIX + bid, next)
    emit()
  },

  /** Spent — BotDetail saw it (and opened the phone's chat sheet, if it could). */
  clearComposerNudge(bid: string) {
    ensure(bid)
    if (!state.composerNudge) return
    state = { ...state, composerNudge: false }
    emit()
  },

  /** Bot switch / test reset. Re-reads both mirrors — it does NOT forget the latch,
   *  and drops the transient nudge so a reload cannot replay it. */
  reset(bid: string) {
    state = fresh(bid)
    emit()
  },
}

/** The one-shot edge that opens the phone's chat sheet. A primitive snapshot on
 *  purpose: no referential-stability trap, and `ensure` re-reading the same flag
 *  cannot cause a spurious re-render. */
export function useComposerNudge(bid: string): boolean {
  return useSyncExternalStore(chartActivity.subscribe, () => snapshotFor(bid).composerNudge)
}

/** Is there anything on the canvas to walk through? Primitive snapshot, same reason
 *  as above: the count itself is nobody's business outside the Workbench. */
export function useHasDrawings(bid: string): boolean {
  return useSyncExternalStore(chartActivity.subscribe, () => snapshotFor(bid).drawCount > 0)
}
